import { FC, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import JobService from "@app/jobs/jobService";
import { JobQueue } from "@app/jobs/job-queues/jobQueue";

export interface JobCancelProps {
  started: boolean;
  jobQueueList: JobQueue[];
  jobIds: number[];
  onCompleted?: () => void;
}

export const JobCancel: FC<React.PropsWithChildren<JobCancelProps>> = (props: JobCancelProps) => {
  const jobService = useRef(new JobService()).current;
  const [newQueueName, setNewQueueName] = useState("");
  const [requeueAfterCancel, setRequeueAfterCancel] = useState(false);

  useEffect(() => {
    setRequeueAfterCancel(false);
    setNewQueueName("");
  }, [props.started]);

  function toastSuccess(message: string) {
    toast("Job(s): " + props.jobIds + message,
      {
        containerId: "z-index-9999",
        onOpen: props.onCompleted,
        type: "success",
      }
    )
  }

  function toastError(message: string) {
    if (message === undefined) {
      toast("An error occured when cancelling or requeuing job(s)",
        {
          containerId: "z-index-9999",
          onOpen: props.onCompleted,
          type: "error",
        },
      );
    }
    else {
      toast("An error occured when cancelling job(s). Error: " + message,
        {
          containerId: "z-index-9999",
          onOpen: props.onCompleted,
          type: "error",
        },
      );
    }
  }

  const cancelJobButtonHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (props.started) {
      jobService.cancelStartedJobById(props.jobIds, requeueAfterCancel).then(() => {
        if (requeueAfterCancel) {
          toastSuccess(" cancelled and requeued successfully.");
        }
        else {
          toastSuccess(" cancelled successfully.");
        }
      }, (error) => {
        toastError(error.responseJSON?.detail);
      }
      );
      return;
    }
    if ((!props.started) && (requeueAfterCancel)) {
      jobService.changeJobQueue(props.jobIds, newQueueName).then(() => {
        toastSuccess(" succesfully cancelled and move to queue: " + newQueueName + ".");
      },
        (error) => {
          toastError(error.responseJSON?.detail);
        }
      );
      return;
    }
    if (!props.started) {
      jobService.cancelQueuedJobById(props.jobIds).then(() => {
        toastSuccess("  cancelled successfully.");
      }, (error) => {
        toastError(error.responseJSON?.detail);
      }
      );
      return;
    }
  }

  return (
    <div className="row mx-0">
      <QESButton
        disabled={props.jobIds.length == 0 || ((requeueAfterCancel) && (newQueueName == "") && (!props.started))}
        onClick={cancelJobButtonHandler}
        className="ms-2 me-1 col-auto"
        qesType="primary-danger"
        qesTestId={"jobs-list__cancel-job-button"}>
          {props.started && requeueAfterCancel ? "Cancel + Requeue" : !props.started && requeueAfterCancel ? "Change Queue" : "Cancel Job(s)"}
        </QESButton>
      <QESCheckbox
        disabled={props.jobIds.length == 0}
        containerClassName="ms-2 me-2 col-auto"
        testId="job-list__requeue-job"
        label="Requeue"
        onChange={(e) => setRequeueAfterCancel(e.target.checked)} checked={requeueAfterCancel} />
      {
        !props.started && (
          <QESSelect
            allowEmpty={true}
            containerClassName="jobs-list__requeue-job-queue-list col-auto"
            testId="jobs-list__requeue-list"
            label=""
            placeholder="Select a Queue"
            onChange={(e) => setNewQueueName(e.target.value)}
            disabled={!requeueAfterCancel}
            value={newQueueName} >
            {props.jobQueueList.map(item => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </QESSelect>
        )
      }
    </div>)
}
